import { ILaunchRequestProps, useLaunchMutation } from '@/redux/api/client/launch';
import { useCallback } from 'react';
import { makeToastProblemMessage } from './util';
import { useLazyGetUpcomingRegistrationsQuery } from '@/redux/api/client/registration';
import useToast from '../useToast';
import { AnalyticsContext } from '@/analytics/constants';
import { useFetchAnalyticsItems } from '@/analytics/useFetchAnalyticsItems';
import { trackCardLaunchAProgramEvent } from '@/analytics/cards';
import { ItemAnalyticsModel, ItemListAnalyticsModel } from '@/@types/client-api';
import { useAppDispatch } from '..';
import libraryEndpoints from '@/redux/api/client/library';
import { useLibraryItems } from '../PDP/useLibraryItems';
import { evictCreditTrackerCode, setCreditTrackerCode } from '../useCreditTrackerCode';
import { useGetUserAddressesQuery } from '@/redux/api/client/userAddresses';
import { useAddToCart } from './useAddToCart';
import { useAuth } from 'react-oidc-context';
import { skipToken } from '@reduxjs/toolkit/query';
import { trackSelectItem } from '@/analytics/ecommerce';

export interface ILaunchProps {
  code?: string;
  isLiveUnregLaunch?: boolean;
  alreadyPurchasedLaunchUrl?: string;
  itemAnalyticsModel?: ItemAnalyticsModel;
  listId?: string;
  listName?: string;
  index?: number;
}

export type LaunchResult =
  | 'noop'
  | 'already-purchased'
  | 'launched'
  | 'error'
  | 'redirected-to-cart';

export const useLaunch = (
  context?: AnalyticsContext,
  creditTrackerCode?: string,
  itemListAnalyticsModel?: ItemListAnalyticsModel
) => {
  const dispatch = useAppDispatch();
  const [launchMutation, { isLoading, isSuccess, isError }] = useLaunchMutation();
  const { showFailMessage } = useToast();
  const [regTrigger] = useLazyGetUpcomingRegistrationsQuery();
  const { getAnalyticItem } = useFetchAnalyticsItems();
  const { hydrateLibrary, selectLibraryItem } = useLibraryItems();
  const { addToCart } = useAddToCart(AnalyticsContext.PDP, creditTrackerCode);

  const trackEvent = useCallback(
    async (
      code: string,
      context?: AnalyticsContext,
      alreadyPurchasedLaunchUrl?: string,
      _itemAnalyticsModel?: ItemAnalyticsModel,
      listId?: string,
      listName?: string,
      index?: number
    ) => {
      // Always fetch AnalyticsModel to ensure we are not using a search provided model with incomplete pricing data when tracking commerce events.
      const data = await getAnalyticItem(code);

      if (data) {
        const isZeroDollar = (!data.price || data.price === 0) && !alreadyPurchasedLaunchUrl;
        trackSelectItem(data, context, listId, listName, itemListAnalyticsModel, index);
        trackCardLaunchAProgramEvent(
          data,
          context,
          isZeroDollar,
          itemListAnalyticsModel,
          !isZeroDollar,
          index
        );
      }
    },
    [getAnalyticItem, itemListAnalyticsModel]
  );

  const auth = useAuth();
  const { data: userAddressData } = useGetUserAddressesQuery(
    auth.isAuthenticated ? undefined : skipToken
  );
  const userHasAddress =
    userAddressData && userAddressData?.items && userAddressData?.items?.length > 0;

  const launch = useCallback(
    async (props: ILaunchProps) => {
      const {
        code,
        isLiveUnregLaunch,
        alreadyPurchasedLaunchUrl: providedLaunchUrl,
        itemAnalyticsModel,
        listId,
        listName,
        index
      } = props;

      if (!code) return 'noop' as const;

      let alreadyPurchasedLaunchUrl = providedLaunchUrl;
      if (!alreadyPurchasedLaunchUrl) {
        const segmentLibraryData = selectLibraryItem(code);
        alreadyPurchasedLaunchUrl = segmentLibraryData?.watchUrl ?? undefined;
      }

      if (context && code) {
        trackEvent(
          code,
          context,
          alreadyPurchasedLaunchUrl,
          itemAnalyticsModel,
          listId,
          listName,
          index
        );
      }

      if (creditTrackerCode) {
        setCreditTrackerCode(code, creditTrackerCode);
      }

      if (alreadyPurchasedLaunchUrl) {
        window.open(alreadyPurchasedLaunchUrl, '_blank');
        return 'already-purchased' as const;
      }

      if (userHasAddress) {
        const windowRef = window.open('/launching', '_blank');
        const launchRequest: ILaunchRequestProps = {
          code,
          body: {
            // TODO: SIREHOME-1075 support sourceCode/keyCode
            sourcecode: '',
            keycode: ''
          }
        };
        const launchResponse = await launchMutation(launchRequest);
        if ('error' in launchResponse) {
          const toastContent = makeToastProblemMessage(
            'launch',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (launchResponse.error as any)?.originalStatus
          );
          showFailMessage(toastContent, 'alert');
          windowRef && windowRef.close();
          return 'error' as const;
        } else if ('data' in launchResponse) {
          if (launchResponse.data.launchUrl) {
            if (windowRef) {
              windowRef.location = launchResponse.data.launchUrl;
            }
            hydrateLibrary([code]);
            evictCreditTrackerCode(code);
          } else {
            const toastContent = makeToastProblemMessage('launch');
            showFailMessage(toastContent, 'alert');
            windowRef && windowRef.close();
            return 'error' as const;
          }
          if (isLiveUnregLaunch) regTrigger();
          dispatch(
            libraryEndpoints.util.invalidateTags([
              { type: 'LibraryItem', id: code },
              { type: 'Library', id: 'List' }
            ])
          );
          return 'launched' as const;
        }

        return 'error' as const;
      } else {
        addToCart({ code, redirectToCart: true });
        return 'redirected-to-cart' as const;
      }
    },
    [
      context,
      creditTrackerCode,
      userHasAddress,
      selectLibraryItem,
      trackEvent,
      addToCart,
      launchMutation,
      showFailMessage,
      regTrigger,
      dispatch,
      hydrateLibrary
    ]
  );

  return { launch, isLoading, isSuccess, isError };
};
